import { useState, useMemo, useEffect } from 'react';
import { MembershipDto, VenueDto, VenueServiceDto } from '@/services/platform/api/model';
import type { BookingState, WhatSheetProps } from '../types';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import { Icon } from '@/components/ui/icon';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from '@/components/ui/radio-group';
import { BookHubs } from '@/components/shared/booking/common/book/hubs';
import { useLocation } from 'react-router-dom';
import { useLocaleStore } from '@/lib/core/stores';
import { getMembership } from '@/services/platform/data/profile';
import { getBookableServices } from '@/services/platform/data/booking';
import { useExploreLocations } from '@/services/platform/data/explore/useExploreLocations';
import { mewsServiceIsSingleOccupancy } from '@/services/platform/data/booking/mews';

export function WhatSheet({
  isOpen,
  onClose,
  cmsProps,
  booking,
  onBookingChange,
  serviceTypeOrder,
}: WhatSheetProps) {
  const t = useClientTranslation();
  const location = useLocation();
  const locale = useLocaleStore(s => s.locale);
  const categoryId = (location.state as { categoryId?: string } | null)?.categoryId;
  const targetVenueNodeKey = (location.state as { venueNodeKey?: string | null } | null)?.venueNodeKey;

  const [services, setServices] = useState<VenueServiceDto[]>([]);
  const ALL_HUB = t('App.Labels.All');

  useEffect(() => {
    getBookableServices({ serviceCategoryNodeKey: categoryId }, locale).then(result => {
      setServices(result ?? []);
    });
  }, [categoryId, locale]);

  /**
   * The list follows the order the editor gave `serviceTypeSteps`, not the order
   * `/reservations/services` returned.
   *
   * A service whose type the editor never authored a block for sorts last rather than first —
   * `indexOf` returning -1 would otherwise promote exactly the services the flow has no steps for.
   * Ties keep the API's own order, since `Array.prototype.sort` is stable.
   */
  const orderedServices = useMemo(() => {
    if (!serviceTypeOrder?.length) return services;
    const rank = new Map(serviceTypeOrder.map((type, index) => [type.toLowerCase(), index]));
    const rankOf = (service: VenueServiceDto) =>
      rank.get((service.serviceType ?? '').toLowerCase()) ?? serviceTypeOrder.length;
    return [...services].sort((a, b) => rankOf(a) - rankOf(b));
  }, [services, serviceTypeOrder]);

  const [membership, setMembership] = useState<MembershipDto | undefined>(undefined);

  useEffect(() => {
    getMembership().then(setMembership);
  }, []);

  const isMember = membership?.isActive === true;

  const isVenueMemberOnly = (venue: VenueDto) => venue.membersOnly ?? false;
  const isDisabled = (venue: VenueDto) => isVenueMemberOnly(venue) && !isMember;

  /**
   * The booking patch that picking `service` produces.
   *
   * A single-occupancy service settles its guest count here rather than leaving it to the How many
   * row, which the flow renders read-only for exactly these services — nothing downstream would
   * otherwise ever set it, and the Continue button gates on `adults > 0`.
   */
  const selectionPatch = (service: VenueServiceDto): Partial<BookingState> => ({
    service,
    venue: service.venue ?? null,
    seatingAreaPublicGuid: service.publicGuid,
    room: service.name,
    date: null,
    time: null,
    ...(mewsServiceIsSingleOccupancy(service.serviceType) ? { adults: 1, children: 0 } : {}),
  });

  const commitSelection = (service: VenueServiceDto) => {
    onBookingChange(selectionPatch(service));
    onClose();
  };

  useEffect(() => {
    if (!orderedServices.length) return;
    const enabled = orderedServices.filter(v => !isDisabled(v));

    // Keep the selection in sync with the venue the user arrived from.
    if (targetVenueNodeKey) {
      if (booking.venue?.venueNodeKey === targetVenueNodeKey) return;
      const preselected = enabled.find(v => v.venue?.venueNodeKey === targetVenueNodeKey);
      if (preselected) onBookingChange(selectionPatch(preselected));
      return;
    }

    if (enabled.length !== 1) return;
    if (booking.venue?.venueNodeKey === enabled[0]?.venue?.venueNodeKey) return;
    onBookingChange(selectionPatch(enabled[0]));
    onClose();
  }, [orderedServices, isMember, targetVenueNodeKey, booking.venue]);

  // The hub chips read "Lisbon Hub" / "Comporta Hub" — the name of the *location node*, which is
  // what `/explore/locations` returns. `venue.location` comes from the same node but the venue
  // catalogue still projects it as the shorter "Lisbon" / "Comporta", so the two are joined on
  // `locationNodeKey` rather than on either name.
  const { locations } = useExploreLocations();

  const hubNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const location of locations) {
      if (location.nodeKey && location.name) map.set(location.nodeKey, location.name);
    }
    return map;
  }, [locations]);

  const hubs = useMemo(() => {
    const names = orderedServices
      .map(s => (s.venue?.locationNodeKey ? hubNameByKey.get(s.venue.locationNodeKey) : undefined))
      .filter((name): name is string => !!name);
    return [ALL_HUB, ...Array.from(new Set(names))];
  }, [orderedServices, hubNameByKey, ALL_HUB]);

  const [activeHub, setActiveHub] = useState(ALL_HUB);

  const filtered = useMemo(() => {
    if (activeHub === ALL_HUB) return orderedServices;
    return orderedServices.filter(
      s => !!s.venue?.locationNodeKey && hubNameByKey.get(s.venue.locationNodeKey) === activeHub
    );
  }, [orderedServices, activeHub, hubNameByKey, ALL_HUB]);

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="h-[80dvh] min-h-[min(650px,100dvh)]">
        <div data-base-ui-swipe-ignore className="flex flex-col gap-[32px] overflow-y-auto px-4 pt-8 pb-25">
          <DrawerHeader className="gap-2 px-0">
            <div className="flex items-center gap-8">
              <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">
                {cmsProps?.label ?? t('App.Labels.What')}
              </DrawerTitle>
              <DrawerClose
                className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full"
                aria-label="Close"
              >
                <Icon
                  name="xmark"
                  width={24}
                  height={24}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000"
                />
              </DrawerClose>
            </div>

            {cmsProps?.description && (
              <DrawerDescription className="text-[16px] text-[#707070]">
                {cmsProps.description}
              </DrawerDescription>
            )}
          </DrawerHeader>

          <BookHubs hubs={hubs} activeHub={activeHub} onHubChange={setActiveHub} />

          {/* Service Venue list — each row is one service, so it is keyed by the service: a venue
              can offer several of them, and keying by venue would select them all at once and
              commit the first whichever one was tapped. */}
          <RadioGroup
            value={booking.service?.publicGuid ?? ''}
            onValueChange={key => {
              const service = filtered.find(v => v.publicGuid === key);
              if (service?.venue) commitSelection(service);
            }}
            className="flex flex-col"
          >
            {filtered.map(service => {
              const membersOnly = service.venue ? isVenueMemberOnly(service.venue) : false;
              const disabled = service.venue ? isDisabled(service.venue) : true;
              const itemId = `what-${service.publicGuid}`;
              const isSelected = !!service.publicGuid && service.publicGuid === booking.service?.publicGuid;

              return (
                <RadioGroupLabel
                  key={service.publicGuid}
                  htmlFor={itemId}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-4 py-4',
                    disabled && 'pointer-events-none opacity-40'
                  )}
                  // `onValueChange` only fires when the value actually changes, so tapping the row
                  // that is already chosen would otherwise do nothing at all — the sheet just sat
                  // there. Confirming the existing answer has to close it too, so that case is
                  // handled here; every other row still goes through the radio group above.
                  //
                  // `preventDefault` stops the label from forwarding the click to its radio, which
                  // would bubble straight back into this handler and commit the selection twice.
                  onClick={event => {
                    if (disabled || !isSelected) return;
                    event.preventDefault();
                    commitSelection(service);
                  }}
                >
                  <RadioGroupItem id={itemId} value={service.publicGuid ?? ''} disabled={disabled} />

                  <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
                    {/* The tag states a property of the service, so it shows for every members-only
                        one — not just the ones this customer cannot book. */}
                    {membersOnly && (
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                        {t('App.Labels.MembersOnly')}
                      </span>
                    )}
                    <span className="font-inter text-jncquoi-neutral-1000 w-full truncate text-[16px] font-semibold tracking-[0.25px]">
                      {service.name}
                    </span>
                    {service.venue?.name && (
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-700 flex w-full items-center gap-1 text-[12px] tracking-[0.5px]">
                        <Icon
                          name="map-pin"
                          width={12}
                          height={12}
                          className="text-jncquoi-neutral-700 shrink-0"
                        />
                        <span className="truncate">{service.venue.name}</span>
                      </span>
                    )}
                  </div>
                </RadioGroupLabel>
              );
            })}
          </RadioGroup>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
